import {
	AfterViewInit,
	Directive,
	ElementRef,
	HostListener,
	Inject,
	Input,
	Renderer2,
	forwardRef
} from "@angular/core";
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	ControlContainer,
	FormControl
} from "@angular/forms";
import { AtlasFormElement } from "@shared/schemas/atlas-form-element";
import {
	FORM_ERRORS,
	FormErrorTypeAssign,
	FormErrorTypes
} from "@shared/utils/forms/form-errors";

@Directive({
	selector: "[appAtlasFormElement]",
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => AtlasFormElementDirective),
			multi: true
		}
	],
	standalone: false
})
export class AtlasFormElementDirective
	implements AfterViewInit, ControlValueAccessor
{
	@Input() formControlName = "";
	@Input() isCustomValidation = false;

	private atlasElement!: AtlasFormElement;

	constructor(
		private _renderer: Renderer2,
		private _elementRef: ElementRef<AtlasFormElement>,
		private _controlContainer: ControlContainer,
		@Inject(FORM_ERRORS) private formErrors: FormErrorTypes
	) {
		this.atlasElement = this._elementRef.nativeElement;
	}

	private _onChange = (_: string | number | boolean | object) => {};
	private _onTouched = () => {};

	private isCheckElement(): boolean {
		return [
			"ATLAS-CHECKBOX",
			"ATLAS-RADIO",
			"ATLAS-TOGGLE",
			"ATLAS-SELECTION-CARD"
		].includes(this.atlasElement.tagName);
	}

	private validateInput(): void {
		const errors = this.control?.errors;
		if (errors) {
			const atlasValidationReport =
				this.atlasElement.getValidationsReport();
			if (Object.keys(atlasValidationReport).length === 0) {
				const keys = Object.keys(errors);
				keys.map((key) => {
					const getError =
						this.formErrors[key as keyof FormErrorTypes];
					const text = getError(errors[key] as FormErrorTypeAssign);
					this.atlasElement.setErrorMessage(text);
				});
			}
		}
	}

	@HostListener("blur")
	handleOnBlur(): void {
		const updateOn = this._controlContainer.control?.root.updateOn;
		if (updateOn && updateOn === "blur") {
			this._onTouched();
			this.validateInput();
		}
	}

	@HostListener("atlas-form-element-value-change")
	handleChangeEvent(): void {
		const value = this.isCheckElement()
			? this.atlasElement.checked
			: this.atlasElement.getElementValue();

		this._onChange(value);
	}

	@HostListener("atlas-selection-card-change")
	handleSelectionCardChangeEvent(): void {
		this._onChange(this.atlasElement.checked);
	}

	registerOnTouched(fn: () => void): void {
		this._onTouched = fn;
	}

	registerOnChange(
		fn: (_: string | number | boolean | object) => NonNullable<unknown>
	): void {
		this._onChange = fn;
	}

	writeValue(value: string): void {
		this._renderer.setProperty(
			this.atlasElement,
			this.isCheckElement() ? "checked" : "value",
			value
		);
	}

	setDisabledState(isDisabled: boolean): void {
		this._renderer.setProperty(this.atlasElement, "disabled", isDisabled);
	}

	get control(): FormControl | null {
		return this._controlContainer.control?.get(
			this.formControlName
		) as FormControl;
	}

	private getValidateInputError(): string {
		const errors = this.control?.errors;
		if (!errors) {
			return "";
		}
		let errorMessage = "";
		Object.keys(errors).forEach((key) => {
			const getError = this.formErrors[key as keyof FormErrorTypes];
			errorMessage += getError(errors[key] as FormErrorTypeAssign);
		});
		return errorMessage;
	}

	ngAfterViewInit() {
		if (this.isCustomValidation) {
			this.atlasElement.addValidator({
				name: "angular-form-validator",
				status: "error",
				getInvalidMessage: () => {
					return this.getValidateInputError();
				},
				validate: () => {
					this.control?.updateValueAndValidity();
					return this.control?.status !== "INVALID";
				}
			});
		}
	}
}
